import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, LogIn, LogOut, Camera, Plus, Check, X, ChevronRight, ImageIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const STATUS_MAP = {
  nao_enviado: { label: 'Não enviado', color: 'bg-zinc-700/50 text-zinc-300' },
  enviado: { label: 'Enviado', color: 'bg-blue-500/20 text-blue-400' },
  aprovado: { label: 'Aprovado', color: 'bg-green-500/20 text-green-400' },
  em_andamento: { label: 'Em andamento', color: 'bg-yellow-500/20 text-yellow-400' },
  concluido: { label: 'Concluído', color: 'bg-purple-500/20 text-purple-400' },
  pago: { label: 'Pago', color: 'bg-emerald-500/20 text-emerald-400' },
};

function compressImage(dataUrl, maxW = 900) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(maxW / img.width, 1);
      const c = document.createElement('canvas');
      c.width = img.width * scale; c.height = img.height * scale;
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL('image/jpeg', 0.7));
    };
    img.src = dataUrl;
  });
}

export default function EmployeePortal() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(() => {
    try { return JSON.parse(localStorage.getItem('orcfacil_employee') || 'null'); } catch { return null; }
  });
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loginForm, setLoginForm] = useState({ login: '', password: '' });
  const [loggingIn, setLoggingIn] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showProposalDetail, setShowProposalDetail] = useState(false);
  const [addingPhoto, setAddingPhoto] = useState(false);
  const [showNewOrc, setShowNewOrc] = useState(false);
  const [newOrcForm, setNewOrcForm] = useState({ titulo: '', cliente_nome: '', obs: '' });
  const photoRef = useRef(null);

  const isLoggedIn = session && session.employee?.company_id === companyId;

  useEffect(() => {
    axios.get(`${BACKEND}/api/employees/portal/${companyId}/info`)
      .then(r => setCompanyInfo(r.data))
      .catch(() => setCompanyInfo({ company_nome: 'Empresa', logo: null }));
  }, [companyId]);

  useEffect(() => {
    if (isLoggedIn) loadProposals();
  }, [isLoggedIn]);

  const loadProposals = async () => {
    setLoadingProposals(true);
    try {
      const r = await axios.get(`${BACKEND}/api/employees/portal/${companyId}/proposals/${session.employee.id}`);
      setProposals(r.data);
    } catch { toast.error('Erro ao carregar orçamentos'); }
    finally { setLoadingProposals(false); }
  };

  const doLogin = async () => {
    if (!loginForm.login || !loginForm.password) { toast.error('Preencha login e senha'); return; }
    setLoggingIn(true);
    try {
      const r = await axios.post(`${BACKEND}/api/employees/portal/login`, { company_id: companyId, ...loginForm });
      const sess = { access_token: r.data.access_token, employee: r.data.employee };
      localStorage.setItem('orcfacil_employee', JSON.stringify(sess));
      setSession(sess);
      toast.success(`Bem-vindo, ${r.data.employee.nome}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login inválido');
    } finally { setLoggingIn(false); }
  };

  const doLogout = () => {
    localStorage.removeItem('orcfacil_employee');
    setSession(null); setProposals([]);
  };

  const openProposalDetail = async (propId) => {
    try {
      const r = await axios.get(`${BACKEND}/api/employees/portal/${companyId}/proposal/${propId}`);
      setSelectedProposal(r.data);
      setShowProposalDetail(true);
    } catch { toast.error('Erro ao carregar orçamento'); }
  };

  const finalizeService = async (propId) => {
    try {
      await axios.put(`${BACKEND}/api/employees/portal/proposals/${propId}/complete`, { employee_id: session.employee.id });
      toast.success('Serviço finalizado! Status: Concluído');
      loadProposals();
      if (selectedProposal?.id === propId) {
        setSelectedProposal(p => ({ ...p, status: 'concluido' }));
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao finalizar'); }
  };

  const handleAddPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedProposal) return;
    setAddingPhoto(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const compressed = await compressImage(ev.target.result);
        await axios.post(`${BACKEND}/api/employees/portal/proposals/${selectedProposal.id}/photos`, {
          employee_id: session.employee.id,
          photo_base64: compressed
        });
        toast.success('Foto adicionada!');
        const r = await axios.get(`${BACKEND}/api/employees/portal/${companyId}/proposal/${selectedProposal.id}`);
        setSelectedProposal(r.data);
        setAddingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch { toast.error('Erro ao adicionar foto'); setAddingPhoto(false); }
    e.target.value = '';
  };

  const createOrcamento = async () => {
    if (!newOrcForm.titulo) { toast.error('Informe o título'); return; }
    try {
      const r = await axios.post(`${BACKEND}/api/employees/portal/proposals/create`, {
        ...newOrcForm,
        employee_id: session.employee.id,
        company_id: companyId,
      });
      toast.success(`Orçamento #${r.data.num} criado!`);
      setShowNewOrc(false);
      setNewOrcForm({ titulo: '', cliente_nome: '', obs: '' });
      loadProposals();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao criar'); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" theme="dark" richColors />
      {/* Header */}
      <header className="bg-zinc-900/95 border-b border-zinc-800 px-4 py-3 flex items-center gap-3">
        <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center shrink-0">
          <FileText size={16} className="text-white" />
        </div>
        {companyInfo?.logo
          ? <img src={companyInfo.logo} alt="Logo" className="h-8 w-auto object-contain" />
          : <span className="font-heading font-bold text-white">{companyInfo?.company_nome || 'Portal'}</span>
        }
        <span className="text-xs text-zinc-500 hidden sm:block">Portal do Funcionário</span>
        {isLoggedIn && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-zinc-400 hidden sm:block">{session.employee.nome}</span>
            <button onClick={doLogout} className="text-zinc-500 hover:text-red-400 p-1.5 transition-colors" title="Sair">
              <LogOut size={16} />
            </button>
          </div>
        )}
      </header>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* LOGIN */}
        {!isLoggedIn && (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <h1 className="font-heading text-2xl font-bold text-white">Área do Funcionário</h1>
              <p className="text-zinc-400 text-sm">{companyInfo?.company_nome}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-4">
              <div>
                <label className="field-label">Login</label>
                <input
                  data-testid="employee-login-input"
                  className="field-input"
                  placeholder="Seu login"
                  value={loginForm.login}
                  onChange={e => setLoginForm(p => ({ ...p, login: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && doLogin()}
                />
              </div>
              <div>
                <label className="field-label">Senha</label>
                <input
                  data-testid="employee-password-input"
                  type="password"
                  className="field-input"
                  placeholder="Sua senha"
                  value={loginForm.password}
                  onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && doLogin()}
                />
              </div>
              <button
                data-testid="employee-login-btn"
                onClick={doLogin}
                disabled={loggingIn}
                className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-colors"
              >
                <LogIn size={18} />
                {loggingIn ? 'Entrando...' : 'Entrar'}
              </button>
            </div>
          </div>
        )}

        {/* DASHBOARD */}
        {isLoggedIn && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-heading text-xl font-bold text-white">Meus Serviços</h1>
                <p className="text-xs text-zinc-500">Olá, {session.employee.nome}</p>
              </div>
              <button
                data-testid="new-orc-portal-btn"
                onClick={() => navigate(`/portal/${companyId}/proposals/new`)}
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white text-sm font-semibold px-3 py-2 rounded-xl transition-colors"
              >
                <Plus size={15} />
                Novo orçamento
              </button>
            </div>

            {loadingProposals ? (
              <div className="flex justify-center py-10"><div className="w-7 h-7 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
            ) : proposals.length === 0 ? (
              <div className="text-center py-14 text-zinc-500">
                <FileText size={36} className="mx-auto mb-3 text-zinc-700" />
                <p className="text-sm">Nenhum orçamento atribuído a você</p>
              </div>
            ) : (
              <div className="space-y-3">
                {proposals.map(p => {
                  const sc = STATUS_MAP[p.status] || STATUS_MAP.nao_enviado;
                  return (
                    <div key={p.id} data-testid={`portal-proposal-${p.id}`} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-white text-sm truncate">{p.titulo}</div>
                          <div className="text-xs text-zinc-500 mt-0.5">#{p.num} · {p.cliente_nome || 'Sem cliente'}</div>
                        </div>
                        <span className={`badge text-xs shrink-0 ${sc.color}`}>{sc.label}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm text-orange-400 font-bold">{brl(p.valor)}</span>
                        <div className="flex gap-2">
                          <button onClick={() => openProposalDetail(p.id)}
                            className="flex items-center gap-1.5 text-xs border border-zinc-700 text-zinc-400 hover:text-white px-2.5 py-1.5 rounded-lg transition-colors">
                            <ChevronRight size={12} /> Ver detalhes
                          </button>
                          <button onClick={() => navigate(`/portal/${companyId}/proposals/${p.id}/edit`)}
                            className="flex items-center gap-1.5 text-xs border border-orange-500/40 text-orange-400 hover:bg-orange-500/10 px-2.5 py-1.5 rounded-lg transition-colors">
                            Editar
                          </button>
                          {(p.status === 'aprovado' || p.status === 'em_andamento') && (
                            <button
                              data-testid={`finalize-btn-${p.id}`}
                              onClick={() => finalizeService(p.id)}
                              className="flex items-center gap-1.5 text-xs bg-purple-600 hover:bg-purple-500 text-white px-2.5 py-1.5 rounded-lg transition-colors font-medium"
                            >
                              <Check size={12} /> Finalizar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Proposal Detail Modal */}
      {showProposalDetail && selectedProposal && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-zinc-900 shrink-0">
            <div>
              <h2 className="font-heading font-semibold text-white text-sm">{selectedProposal.titulo}</h2>
              <div className="text-xs text-zinc-500">#{selectedProposal.num} · {selectedProposal.cliente_nome || '—'}</div>
            </div>
            <button onClick={() => { setShowProposalDetail(false); loadProposals(); }} className="text-zinc-400 hover:text-white p-2">
              <X size={20} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Items */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-800 text-xs text-zinc-500 font-medium">ITENS DO SERVIÇO</div>
              {(selectedProposal.itens || []).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/50 last:border-0">
                  <div className="flex-1 text-sm text-white">{item.descricao}</div>
                  <div className="text-xs text-zinc-500">{item.quantidade} {item.unidade}</div>
                  <div className="text-xs font-mono text-orange-400 ml-3">{brl(item.valor_total)}</div>
                </div>
              ))}
              <div className="flex justify-between px-4 py-3 border-t border-zinc-800">
                <span className="text-sm font-bold text-white">Total</span>
                <span className="font-mono text-orange-400 font-bold">{brl(selectedProposal.valor)}</span>
              </div>
            </div>

            {/* Observations */}
            {selectedProposal.obs && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <div className="text-xs text-zinc-500 mb-1">OBSERVAÇÕES</div>
                <p className="text-sm text-zinc-300 whitespace-pre-line">{selectedProposal.obs}</p>
              </div>
            )}

            {/* Execution photos */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs text-zinc-400 font-medium flex items-center gap-1.5">
                  <ImageIcon size={13} />
                  FOTOS DA EXECUÇÃO ({(selectedProposal.execution_photos || []).length})
                </div>
                <label className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg cursor-pointer transition-colors ${addingPhoto ? 'opacity-50' : 'bg-orange-500/10 text-orange-400 border border-orange-500/30 hover:bg-orange-500/20'}`}>
                  <Camera size={13} />
                  {addingPhoto ? 'Enviando...' : '+ Foto'}
                  <input ref={photoRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleAddPhoto} disabled={addingPhoto} />
                </label>
              </div>
              {(selectedProposal.execution_photos || []).length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {selectedProposal.execution_photos.map((photo, idx) => (
                    <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-zinc-800">
                      <img src={photo} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-zinc-600 text-center py-3">Nenhuma foto adicionada</p>
              )}
            </div>
          </div>

          {/* Footer actions */}
          <div className="shrink-0 p-4 border-t border-zinc-800 bg-zinc-900/95 space-y-2">
            {(selectedProposal.status === 'aprovado' || selectedProposal.status === 'em_andamento') && (
              <button
                data-testid="finalize-service-btn"
                onClick={() => finalizeService(selectedProposal.id)}
                className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 rounded-xl transition-colors"
              >
                <Check size={18} />
                Finalizar Serviço
              </button>
            )}
            {selectedProposal.status === 'concluido' && (
              <div className="w-full flex items-center justify-center gap-2 bg-purple-500/20 border border-purple-500/30 text-purple-400 font-semibold py-3 rounded-xl">
                <Check size={18} /> Serviço Concluído
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
